import React from "react";
import { Lightbulb, Compass, AlertTriangle, CheckCircle2, Sparkles, ArrowRight, Check, Quote } from "lucide-react";

export default function RichBody({ blocks, T }) {
  const renderList = (items, keyPrefix) => (
    <ul className="mt-3 space-y-2">
      {items.map((it, i) => {
        if (typeof it === "string") {
          return (
            <li key={`${keyPrefix}-${i}`} className="flex items-start gap-3 text-nm-ink/85 leading-relaxed">
              <span className={`mt-2 h-1.5 w-1.5 rounded-full ${T.dot} shrink-0`} />{it}
            </li>
          );
        }
        return (
          <li key={`${keyPrefix}-${i}`} className="text-nm-ink/85 leading-relaxed">
            <div className="flex items-start gap-3">
              <span className={`mt-2 h-1.5 w-1.5 rounded-full ${T.dot} shrink-0`} />
              <span className="font-medium">{it.label}</span>
            </div>
            {it.sub && (
              <ul className="mt-2 ml-6 space-y-1.5">
                {it.sub.map((s, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-nm-neutral text-[15px] leading-relaxed">
                    <span className={`mt-2 h-1 w-1 rounded-full ${T.dot} opacity-50 shrink-0`} />{s}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );

  const renderSub = (bb, j) =>
    bb.t === "ul"
      ? <div key={j}>{renderList(bb.items, `sub-${j}`)}</div>
      : <p key={j} className="text-nm-ink/85 leading-relaxed mt-3 text-[17px]">{bb.text}</p>;

  const renderBlock = (blk, i) => {
    switch (blk.t) {
      case "h2":
        return (
          <div key={i} className={`mt-12 pt-7 border-t ${T.sectionRule || "border-nm-primary/15"}`}>
            <h2 className={`flex items-center gap-2.5 text-sm font-semibold uppercase tracking-[0.14em] ${T.headerAccent || T.accent}`}>
              <span className={`h-4 w-1.5 rounded-full ${T.headerBar || T.dot}`} />
              {blk.text}
            </h2>
          </div>
        );
      case "h3": {
        const isSub = /^\s*\d+\s*[.)]\s+/.test(blk.text || "");
        if (isSub)
          return (
            <h3 key={i} className={`font-serif text-xl font-semibold mt-8 mb-1 leading-snug ${T.headerAccent || T.accent}`}>
              {blk.text}
            </h3>
          );
        return (
          <h3 key={i} className="font-serif text-2xl font-semibold mt-12 mb-1 leading-snug flex items-baseline gap-3 text-nm-primary">
            <span className="self-stretch mt-1.5 w-1 rounded-full shrink-0 bg-nm-primary" style={{ minHeight: "1.4rem" }} />
            <span>{blk.text}</span>
          </h3>
        );
      }
      case "numbered":
        return (
          <div key={i} className="mt-6 space-y-4">
            {(blk.items || []).map((it, ni) => (
              <div key={ni} className="flex gap-4 sm:gap-5">
                <div className={`shrink-0 h-11 w-11 rounded-full ${T.headerBar} text-white font-serif text-xl flex items-center justify-center`}>{it.n ?? ni + 1}</div>
                <div className="flex-1 rounded-2xl border border-white/70 bg-gradient-to-br from-white/75 to-white/25 px-5 sm:px-6 py-4">
                  <h4 className="font-serif text-xl leading-snug text-nm-ink">{it.title}</h4>
                  {it.lead && <p className="mt-1.5 text-nm-ink/80 leading-relaxed">{it.lead}</p>}
                  {it.sub && <p className="mt-2.5 text-sm font-medium text-nm-neutral">{it.sub}</p>}
                  {it.bullets?.length > 0 && (
                    <ul className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {it.bullets.map((b, bi) => (
                        <li key={bi} className="flex items-start gap-2.5 text-[15px] leading-relaxed text-nm-ink/85">
                          <span className={`mt-2 h-1.5 w-1.5 rounded-full ${T.dot} shrink-0`} />{b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {it.note && <p className="mt-3 text-[14px] leading-relaxed text-nm-neutral">{it.note}</p>}
                </div>
              </div>
            ))}
          </div>
        );
      case "scripts":
        return (
          <div key={i} className="mt-5 space-y-4" data-testid="scripts-block">
            {(blk.items || []).map((s, si) => (
              <div key={si} className={`rounded-2xl border ${T.tint} p-5 sm:p-6`}>
                <div className="mb-3 flex items-center gap-2">
                  <Quote size={15} className={T.headerAccent || T.accent} />
                  <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${T.headerAccent || T.accent}`}>{s.label}</span>
                </div>
                <p className="font-serif text-[19px] leading-[1.9] text-nm-ink/90">
                  {s.text.split(/(\[[^\]]+\])/g).map((part, k) =>
                    part.startsWith("[") && part.endsWith("]")
                      ? <span key={k} className={`mx-0.5 inline-block rounded-md border border-white/70 bg-white/80 px-1.5 py-0.5 text-[15px] font-medium ${T.headerAccent || T.accent}`}>{part}</span>
                      : <span key={k}>{part}</span>
                  )}
                </p>
              </div>
            ))}
          </div>
        );
      case "p":
        return <p key={i} className="text-nm-ink/85 leading-relaxed mt-3 text-[17px]">{blk.text}</p>;
      case "product":
        return (
          <div key={i} className="mt-8 overflow-hidden rounded-3xl border border-nm-plum/20 bg-gradient-to-br from-[#efe8fb] via-white to-[#eaf3ee] nm-shadow-sm" data-testid="product-cta">
            <div className="p-7 sm:p-9">
              <div className="inline-flex items-center gap-2 rounded-full bg-nm-plum/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-nm-plum">
                <Sparkles size={13} /> {blk.eyebrow || "Digital tool"}
              </div>
              <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <h3 className="font-serif text-3xl leading-tight text-nm-ink">{blk.title}</h3>
                {blk.price && <span className="font-serif text-2xl text-nm-plum">{blk.price}</span>}
              </div>
              {blk.desc && <p className="mt-3 max-w-2xl text-[17px] leading-relaxed text-nm-ink/80">{blk.desc}</p>}
              {blk.bullets?.length > 0 && (
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {blk.bullets.map((b, bi) => (
                    <li key={bi} className="flex items-start gap-2.5 text-[15px] leading-relaxed text-nm-ink/85">
                      <Check size={17} className="mt-0.5 shrink-0 text-nm-green" />{b}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <a href={blk.cta_url} target="_blank" rel="noopener noreferrer" data-testid="product-cta-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-nm-plum px-6 py-3 text-sm font-medium text-white transition-[filter,transform] duration-200 hover:brightness-110 hover:-translate-y-0.5">
                  {blk.cta_label || "Explore the tool"}
                  <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                </a>
                {blk.note && <span className="text-[13px] text-nm-neutral">{blk.note}</span>}
              </div>
            </div>
          </div>
        );
      case "defcards":
        return (
          <div key={i} className="mt-5 grid sm:grid-cols-2 gap-3.5">
            {(blk.items || []).map((c, ci) => (
              <div key={ci} className="rounded-2xl border border-white/70 bg-gradient-to-br from-white/75 to-white/30 px-5 py-4">
                <div className={`font-serif text-lg leading-snug ${T.headerAccent || T.accent}`}>{c.term}</div>
                {c.desc && <p className="text-nm-ink/80 leading-relaxed mt-1.5 text-[15px]">{c.desc}</p>}
                {c.sub && <p className="mt-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-nm-neutral">{c.sub}</p>}
                {c.bullets?.length > 0 && (
                  <ul className="mt-1.5 space-y-1">
                    {c.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-start gap-2 text-[14px] leading-relaxed text-nm-ink/80">
                        <span className={`mt-2 h-1 w-1 rounded-full ${T.dot} shrink-0`} />{b}
                      </li>
                    ))}
                  </ul>
                )}
                {c.note && <p className="text-nm-neutral leading-relaxed mt-2.5 text-[14px]">{c.note}</p>}
              </div>
            ))}
          </div>
        );
      case "table":
        return (
          <div key={i} className="mt-5 overflow-hidden rounded-2xl border border-white/70">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-nm-ink/[0.05] border-b border-white/70">
                  {(blk.headers || []).map((h, hi) => (
                    <th key={hi} className={`px-4 py-3 text-xs font-semibold uppercase tracking-[0.1em] ${T.headerAccent || T.accent} ${hi === 0 ? "w-1/3" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(blk.rows || []).map((r, ri) => (
                  <tr key={ri} className={ri % 2 ? "bg-white/45" : "bg-white/70"}>
                    {r.map((cell, ci) => (
                      <td key={ci} className={`px-4 py-3 align-top leading-relaxed ${ci === 0 ? `font-medium ${T.headerAccent || T.accent}` : "text-nm-ink/85 text-[15px]"}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "groups":
        return (
          <div key={i} className="mt-4 grid sm:grid-cols-2 gap-4">
            {(blk.items || []).map((g, gi) => (
              <div key={gi} className="rounded-2xl border border-white/70 bg-gradient-to-br from-white/70 to-white/25 px-5 py-4">
                <div className={`text-xs font-semibold uppercase tracking-[0.14em] mb-2.5 ${T.headerAccent || T.accent}`}>{g.label}</div>
                <ul className="space-y-1.5">
                  {(g.items || []).map((it, k) => (
                    <li key={k} className="flex items-start gap-2.5 text-nm-ink/85 leading-relaxed">
                      <span className={`mt-2 h-1.5 w-1.5 rounded-full ${T.dot} shrink-0`} />{it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );
      case "ul": {
        const simple = blk.items.every((it) => typeof it === "string");
        const prev = blocks[i - 1] || {};
        const prev2 = blocks[i - 2] || {};
        const ctxText = `${prev.text || ""} ${prev.title || ""} ${prev2.text || ""} ${prev2.title || ""}`;
        const isGoals = simple && /you should be able to|by the end of|pulling the section together/i.test(ctxText);
        if (simple && (blk.items.length >= 8 || isGoals)) {
          return (
            <div key={i} className="mt-4 rounded-2xl border border-white/70 bg-gradient-to-br from-white/70 to-white/25 px-6 py-5">
              <ul className="sm:columns-2 sm:gap-x-10">
                {blk.items.map((it, k) => (
                  <li key={k} className="flex items-start gap-3 text-nm-ink/85 leading-relaxed mb-2.5 break-inside-avoid">
                    {isGoals
                      ? <CheckCircle2 size={18} className="text-[#C2952B] shrink-0 mt-0.5" strokeWidth={2.25} />
                      : <span className={`mt-2 h-1.5 w-1.5 rounded-full ${T.dot} shrink-0`} />}
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        return <div key={i}>{renderList(blk.items, i)}</div>;
      }
      case "ol":
        return (
          <ol key={i} className="mt-3 space-y-2.5 list-none">
            {blk.items.map((it, k) => {
              const label = typeof it === "string" ? it : it.label;
              const sub = typeof it === "object" ? it.sub : null;
              return (
                <li key={k} className="text-nm-ink/85 leading-relaxed">
                  <div className="flex items-start gap-3">
                    <span className={`font-serif text-lg ${T.accent50} w-5 shrink-0 leading-snug`}>{k + 1}</span>
                    <span className="flex-1">{label}</span>
                  </div>
                  {sub && (
                    <ul className="mt-2 ml-8 space-y-1.5">
                      {sub.map((s, j) => (
                        <li key={j} className="flex items-start gap-2.5 text-nm-neutral text-[15px] leading-relaxed">
                          <span className={`mt-2 h-1 w-1 rounded-full ${T.dot} opacity-50 shrink-0`} />{s}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        );
      case "steps":
        return (
          <div key={i} className="mt-5 space-y-7">
            {blk.items.map((s, si) => (
              <div key={si} className="flex gap-4">
                <span className={`font-serif text-2xl ${T.accent50} w-8 shrink-0 leading-none pt-1`}>{si + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl text-nm-ink leading-snug">{s.title}</div>
                  {(s.blocks || []).map(renderSub)}
                </div>
              </div>
            ))}
          </div>
        );
      case "callout": {
        const box = blk.tone === "simple" ? T.simpleBox : blk.tone === "start" ? T.startBox : T.tint;
        return (
          <div key={i} className={`mt-8 rounded-2xl border ${box} p-6`}>
            {blk.title && (
              <div className="flex items-center gap-2 text-nm-ink font-medium">
                {blk.tone === "simple"
                  ? <Lightbulb size={17} className={T.simpleIcon} />
                  : <Compass size={17} className={T.startIcon} />}
                {blk.title}
              </div>
            )}
            {blk.text && <p className="text-nm-ink/80 leading-relaxed mt-2">{blk.text}</p>}
            {blk.items && renderList(blk.items, `co-${i}`)}
            {blk.footer && <p className="text-nm-ink/80 leading-relaxed mt-3">{blk.footer}</p>}
          </div>
        );
      }
      case "mistakes":
        return (
          <div key={i} className="mt-8">
            <div className={`flex items-center gap-2 ${T.accent} font-medium text-sm uppercase tracking-wide mb-3`}>
              <AlertTriangle size={16} /> {blk.title || "Common mistakes"}
            </div>
            <ul className="space-y-2.5">
              {blk.items.map((it, k) => (
                <li key={k} className="flex items-start gap-3 text-nm-ink/85 leading-relaxed">
                  <AlertTriangle size={15} className="text-nm-coral shrink-0 mt-1" />{it}
                </li>
              ))}
            </ul>
          </div>
        );
      default:
        return null;
    }
  };

  return <div className="mt-4" data-testid="rich-body">{blocks.map(renderBlock)}</div>;
}
